import { useState } from 'react';
import { pilotFileExtensions } from '@common/utils/fileExtensions';
import type { IMedication } from '../../../../../interfaces/medication';
import type { ICustomControlProcedureAddEditProps } from '../../../interfaces/customControlProcedureAddEditProps';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../../translate';
import { useEmailAddressControlComponent } from '../../../../../../utils/hooks/useEmailAddressControlComponent';
import { useEpisodeContext } from '../../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../../elements/common';
import { FormContextProvider } from '../../../../../elements/formContext';
import { TextAreaField } from '../../../../../fields/TextArea';
import { ProcessContainer } from '../../../../episodes/common';
import { ProfessionalSummaryData } from '../../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { GraderSelector } from '../../../utils/v1/GraderSelector';
import { MedicationsAddEdit } from '../../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditProcedureProcessContainer } from '../../../utils/v1/NewOrEditProcedureProcessContainer';
import { ServiceOrganizationConsumerSelector } from '../../../utils/v1/ServiceOrganizationConsumerSelector';

export function PilotExaminationV1ProcedureAddEdit(props: ICustomControlProcedureAddEditProps) {
    const { procedure, errors, handleChangeCustomControl, validateOneCustomControl } = props;

    const [buttonDisabled, setButtonDisabled] = useState(false);
    const { patient } = useEpisodeContext();

    const { EmailAddressControlProps, EmailAddressControl, validateEmailAddress } = useEmailAddressControlComponent({
        emailAddress: procedure.customControlData.emailadres
            ? procedure.customControlData.emailadres
            : patient.emailAddress,
        handleChangeCustomControl,
        validateOneCustomControl,
    });

    return (
        <Form onSubmit={() => null}>
            <NewOrEditProcedureProcessContainer
                currentProcedure={props.currentProcedure}
                isBioGrader={props.isBioGrader}
                isFirstItem={props.isFirstItem}
                process={props.process}
                service={props.service}
            >
                {addEditForm()}
            </NewOrEditProcedureProcessContainer>

            {!props.isBioGrader && props.request.isDirectProcedureAdded && (
                <EmailAddressControl
                    {...EmailAddressControlProps}
                    error={errors.get('customControlData.emailadres')}
                />
            )}

            <ProcessContainer noBorderTop>
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.procedure}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveProcedure('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveProcedure('final');
                            }}
                        />,
                    ]}
                    hideButtons={props.isBioGrader}
                />
            </ProcessContainer>
        </Form>
    );

    function addEditForm() {
        return (
            <>
                <CareProviderSelector
                    {...props}
                    entity={props.procedure}
                />

                <ServiceOrganizationConsumerSelector {...props} />

                <AssetDropzoneWithActions
                    acceptedFiles={pilotFileExtensions}
                    assets={props.procedure.assets}
                    assetsBaseUrl={FilesUrlBase.procedures}
                    headerText={getServiceLabel(
                        props.service.code,
                        'procedureAttachment',
                        translateKey('procedure.procedure')
                    )}
                    onChange={async (assets) => await props.handleChange('assets', assets)}
                    onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                    processActivityId={props.procedure.id}
                    service={props.service}
                />

                {props.request.isDirectProcedureAdded && (
                    <>
                        <MedicationsAddEdit
                            entity={ProcessEntity.procedure}
                            entityId={props.procedure.id}
                            errors={props.errors}
                            handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                                await props.handleChange('patientUsesMedication', patientUsesMedication)
                            }
                            onChange={async (medications: IMedication[]) =>
                                await props.handleChange('procedureMedications', medications)
                            }
                            patientUsesMedication={props.procedure.patientUsesMedication}
                            validateMedications={async () => await props.validateOne('procedureMedications')}
                            validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                        />
                        <ProfessionalSummaryData
                            entity={ProcessEntity.procedure}
                            entityId={props.procedure.id}
                            errors={props.errors}
                            handleChangeAllergies={async (allergies) =>
                                await props.handleChange('allergies', allergies)
                            }
                            handleChangeEpisodes={async (episodesAndJournal) =>
                                await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                            }
                            handleChangeLabResults={async (labResults) =>
                                await props.handleChange('requestLabResults', labResults)
                            }
                            requestId={props.request.id}
                            validateEpisodesAndJournals={async () =>
                                await props.validateOne('requestEpisodesAndJournal')
                            }
                        />
                    </>
                )}

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'customControlData',
                        item: props.procedure.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <TextAreaField
                        fieldKey="anamnese"
                        label={translateKey('carePlan.anamnese')}
                        placeholder={translateKey('carePlan.anamnese')}
                    />

                    <TextAreaField
                        fieldKey="metingen"
                        label="Metingen"
                        placeholder="Metingen"
                    />

                    <TextAreaField
                        fieldKey="toelichting"
                        label={translateKey('common.remarks')}
                        placeholder={translateKey('common.remarks')}
                    />
                </FormContextProvider>

                <GraderSelector
                    {...props}
                    careProviderAndRequesterAndSelectedProvider={{ ...props.procedure }}
                    procedurePerformerOrganizationId={props.organizationId}
                />
            </>
        );
    }

    async function saveProcedure(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedProcedure = await props.validateProcedure(saveType);
        if (!validatedProcedure) {
            setButtonDisabled(false);

            return;
        }

        if (props.request.isDirectProcedureAdded) {
            const isValidatedEmailAddress = validateEmailAddress();
            if (!isValidatedEmailAddress) {
                setButtonDisabled(false);

                return;
            }
        }

        const requestId = await props.onSaveProcedure(saveType);
        setButtonDisabled(false);
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.procedureSubmitted',
                props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await props.onSave(requestId);
    }
}

export function pilotExaminationV1ProcedureInitializeCustomControlData() {
    return {
        emailadres: null,
        anamnese: null,
        metingen: null,
        toelichting: null,
    };
}
